import { useState, useCallback } from 'react'
import supabase, { getCached, setCached, lsGet, lsSet } from '../supabase/supabase'

const LAYOUT_CACHE_PREFIX = 'layout_'

export function useLayoutSave() {
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [saveStatus, setSaveStatus] = useState(null)

  const saveLayout = useCallback(async (layoutId, data) => {
    if (!layoutId) return { success: false, error: 'No layout ID provided.' }
    setIsSaving(true); setSaveStatus(null)
    try {
      const payload = { updated_at: new Date().toISOString() }
      if (data.logoData !== undefined) payload.logo_data = data.logoData
      if (data.menuItems !== undefined) payload.menu_items = data.menuItems
      if (data.menuStyles !== undefined) payload.menu_styles = data.menuStyles
      if (data.socialLinks !== undefined) payload.social_links = data.socialLinks
      if (data.footerData !== undefined) payload.footer_data = data.footerData

      const { error } = await supabase
        .from('websites_1772750000000')
        .update(payload)
        .eq('id', layoutId)

      if (error) throw new Error(`Database error saving layout: ${error.message}`)

      // Update caches optimistically
      const cacheKey = `${LAYOUT_CACHE_PREFIX}${layoutId}`
      const existing = getCached(cacheKey) || lsGet(cacheKey) || {}
      const merged = { ...existing, ...payload }
      setCached(cacheKey, merged)
      lsSet(cacheKey, merged)

      setSaveStatus('success')
      return { success: true }
    } catch (err) {
      console.error('Save layout failed:', err)
      setSaveStatus('error')
      return { success: false, error: err.message || 'Network or database error.' }
    } finally {
      setIsSaving(false)
      setTimeout(() => setSaveStatus(null), 3000)
    }
  }, [])

  const loadLayout = useCallback(async (layoutId) => {
    if (!layoutId) return null
    const cacheKey = `${LAYOUT_CACHE_PREFIX}${layoutId}`

    // 1. Return from in-memory cache instantly
    const memCached = getCached(cacheKey)
    if (memCached) {
      // Background revalidate
      ;(async () => {
        try {
          const { data } = await supabase
            .from('websites_1772750000000')
            .select('logo_data,menu_items,menu_styles,social_links,footer_data,layout_data,netlify_site_id,netlify_url,netlify_subdomain,custom_domain,is_published')
            .eq('id', layoutId)
            .single()
          if (data) { setCached(cacheKey, data); lsSet(cacheKey, data) }
        } catch {}
      })()
      return _parseLayout(memCached)
    }

    // 2. Try localStorage cache
    const lsCached = lsGet(cacheKey)
    if (lsCached) {
      setCached(cacheKey, lsCached)
      // Background revalidate
      ;(async () => {
        try {
          const { data } = await supabase
            .from('websites_1772750000000')
            .select('logo_data,menu_items,menu_styles,social_links,footer_data,layout_data,netlify_site_id,netlify_url,netlify_subdomain,custom_domain,is_published')
            .eq('id', layoutId)
            .single()
          if (data) { setCached(cacheKey, data); lsSet(cacheKey, data) }
        } catch {}
      })()
      return _parseLayout(lsCached)
    }

    // 3. Fresh fetch
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('websites_1772750000000')
        .select('logo_data,menu_items,menu_styles,social_links,footer_data,layout_data,netlify_site_id,netlify_url,netlify_subdomain,custom_domain,is_published')
        .eq('id', layoutId)
        .single()

      if (error && error.code !== 'PGRST116') throw new Error(`DB error: ${error.message}`)
      if (!data) return null

      setCached(cacheKey, data)
      lsSet(cacheKey, data)
      return _parseLayout(data)
    } catch (err) {
      console.error('Load layout failed:', err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  return { saveLayout, loadLayout, isSaving, isLoading, saveStatus }
}

function _parseLayout(data) {
  const legacy = data.layout_data || {}
  const activeUrl = data.custom_domain
    ? `https://${data.custom_domain}`
    : data.netlify_subdomain
      ? `https://${data.netlify_subdomain}`
      : data.netlify_url || null

  return {
    logo_data: data.logo_data || legacy.logo_data || null,
    menu_items: data.menu_items || legacy.menu_items || null,
    menu_styles: data.menu_styles || legacy.menu_styles || null,
    social_links: data.social_links || legacy.social_links || null,
    footer_data: data.footer_data || legacy.footer_data || null,
    netlify_site_id: data.netlify_site_id || null,
    netlify_subdomain: data.netlify_subdomain || null,
    netlify_url: activeUrl,
    is_published: data.is_published || false,
  }
}